import { useCallback, useEffect, useMemo, useState } from 'react'
import Cookies from 'js-cookie'

import Footer from '../layout/Footer'
import Header from '../layout/Header'
import { AssessmentContainer, PageContent } from '../styles'
import { useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import MainAssignmentSection from './MainAssignmentSection'
import AssignmentTocModal from 'components/TableOfContentModals/AssignmentTocModal'
import { useAppDispatch } from 'app/hooks'
import { useContext } from 'react'
import socketKeys from 'components/Assessment/socket-keys'

import {
  // clearAssignmentDataAction,
  clearAllAssignmentAnswers,
  clearCurrentAssignmentPageAnswers,
  getAssignmentDataAction,
  setAssignmentPageId,
  updateAssignmentQuestionAnswer,
  updateAssignmentPageAction
} from 'app/reducer/assignmentSlice'
import { SelectAssignmentState } from 'app/reducer/assignmentSlice'
// import { getCmsTemplatesAction } from 'store/actionCreators/cmsAssessment'
import {
  AssignmentBodyItem,
  AssignmentPage,
  QuestionBodyItem
} from 'interfaces/assignments'
import { isQuestionSetPage, isTeacherGuidePage } from '../utils'
import { ViewMode } from 'interfaces/app'
import {
  submitAssignmentAnswers,
  submitAssignmentPartialAnswers,
  upsertAssignmentAnnotations
} from 'services/assignments.api'
import PauseExitModal from 'components/Assessment/models/PauseExitModal'
import FinishModal from 'components/Assessment/models/FinishModal'
import ClearModal from 'components/Assessment/models/ClearModal'
import { QuestionnaireContext } from 'components/Assessment/QuestionnaireContext'
import UnAvailableTestModal from 'components/Assessment/models/UnAvailableTest'

const AssignmentContainer = () => {
  const dispatch = useAppDispatch()
  const {
    setShowPauseExitModal,
    socketRef,
    isSocketReady,
    isPausedByTeacher,
    setIsPausedByTeacher,
    setShowUnAvailableTestModal
  } = useContext(QuestionnaireContext)

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [showHistoryModal, setShowHistoryModal] = useState(false)
  const [showQuestionSelectorModal, setShowQuestionSelectorModal] =
    useState(false)
  const [showFinishModal, setShowFinishModal] = useState(false)
  const [showClearModal, setShowClearModal] = useState(false)
  const [isSubmittingAssignment, setIsSubmittingAssignment] = useState(false)
  const [pendingQuestionScrollTarget, setPendingQuestionScrollTarget] =
    useState<{
      pageId: number
      questionNumber: number
      questionIndexInPage: number
    } | null>(null)

  // const cmsAssessment = useSelector((state: RootState) => state.cmsAssessment)
  const { assignment, pageIdx, currentPage } = useSelector(
    SelectAssignmentState
  )

  const {
    SOCKET_STUDENT_UPDATE_ANSWER,
    SOCKET_STUDENT_UPDATE_POSITION_QUESTION,
    SOCKET_CLASS_BY_ID_CHANNEL,
    SOCKET_TEACHER_PAUSE_RESUME,
    SOCKET_TEACHER_FORCE_SUBMIT,
    SOCKET_TEACHER_KICK_STUDENT,
    SOCKET_STUDENT_JOIN_ROOM,
    SOCKET_TEACHER_CLEAR_ANSWER,
    SOCKET_TEACHER_VIEW_UPDATE
  } = socketKeys

  const navigate = useNavigate()
  const { title, total_pages } = assignment || {}

  /**
   * Header page number should reflect only student-visible pages.
   * Student pages include: section + question_set.
   * Teacher guide sections are intentionally excluded from both current and total.
   */
  const studentPages = useMemo(() => {
    return assignment?.pages?.filter(
      (page: AssignmentPage) => !isTeacherGuidePage(page)
    )
  }, [assignment?.pages])

  const currentStudentPageNumber = useMemo(() => {
    if (!currentPage || isTeacherGuidePage(currentPage)) {
      return undefined
    }

    const studentPageIndex = studentPages.findIndex(
      (page: AssignmentPage) => page.id === currentPage.id
    )

    return studentPageIndex >= 0 ? studentPageIndex + 1 : undefined
  }, [currentPage, studentPages])

  const totalStudentPages = studentPages?.length || undefined

  const teacherGuidePages = useMemo(() => {
    return assignment?.pages?.filter((page: AssignmentPage) =>
      isTeacherGuidePage(page)
    )
  }, [assignment?.pages])

  const currentTeacherGuidePageNumber = useMemo(() => {
    if (!currentPage || !isTeacherGuidePage(currentPage)) {
      return undefined
    }

    const teacherGuidePageIndex = teacherGuidePages.findIndex(
      (page: AssignmentPage) => page.id === currentPage.id
    )

    return teacherGuidePageIndex >= 0 ? teacherGuidePageIndex + 1 : undefined
  }, [currentPage, teacherGuidePages])

  const totalTeacherGuidePages = teacherGuidePages?.length || undefined

  const headerCurrentPage = isTeacherGuidePage(currentPage)
    ? currentTeacherGuidePageNumber
      ? `T${currentTeacherGuidePageNumber}`
      : undefined
    : currentStudentPageNumber

  const headerTotalPages = isTeacherGuidePage(currentPage)
    ? totalTeacherGuidePages
      ? `T${totalTeacherGuidePages}`
      : undefined
    : totalStudentPages

  const { id } = useParams()

  const getFirstQuestionIdForPage = useCallback((page?: AssignmentPage) => {
    /**
     * Assignments can contain section pages and mixed body items, so the
     * position event should only be emitted when the active page is a
     * question-set page with at least one real question item available.
     */
    if (!page || !isQuestionSetPage(page) || !page.body_items?.length) {
      return null
    }

    const firstQuestion = page.body_items.find(
      (item: AssignmentBodyItem | QuestionBodyItem) =>
        item.body_item_type === 'question'
    )

    if (!firstQuestion?.id) {
      return null
    }

    return firstQuestion.id
  }, [])

  useEffect(() => {
    if (!id) {
      return
    }

    const classroomId = Cookies.get('classroom_id')
    dispatch(getAssignmentDataAction(id, classroomId))
  }, [dispatch, id])

  useEffect(() => {
    const firstQuestionId = getFirstQuestionIdForPage(currentPage)
    const classroom_id = Cookies.get('classroom_id')
    const student_id = Cookies.get('student_id')

    /**
     * Mirror formative and benchmark position updates for assignments, but
     * only for question-set pages that actually expose at least one question.
     * Section and teacher-guide pages intentionally do not emit this event.
     */
    if (!isSocketReady || !firstQuestionId || !classroom_id || !student_id || !id) {
      return
    }

    const socketInstance = socketRef.current

    socketInstance?.emit(SOCKET_STUDENT_UPDATE_POSITION_QUESTION, {
      classroom_id: Number(classroom_id),
      test_type: 'assignment',
      test_id: Number(id),
      question_id: firstQuestionId,
      student_id: Number(student_id)
    })
  }, [
    currentPage,
    isSocketReady,
    socketRef,
    getFirstQuestionIdForPage,
    id,
    SOCKET_STUDENT_UPDATE_POSITION_QUESTION
  ])

  const assessmentName = title

  const onNextPagePress = () => {
    const nextPage = assignment?.pages
      .filter(
        (page: AssignmentPage) =>
          Number(page.order_index) > Number(currentPage.order_index)
      )
      .sort(
        (a: AssignmentPage, b: AssignmentPage) => a.order_index - b.order_index
      )[0]

    if (!nextPage) {
      return
    }

    dispatch(setAssignmentPageId(nextPage.id))
  }

  const onBackQuestionPress = () => {
    const previousPage = assignment.pages
      .filter(
        (page: AssignmentPage) =>
          Number(page.order_index) < Number(currentPage.order_index)
      )
      .sort(
        (a: AssignmentPage, b: AssignmentPage) => b.order_index - a.order_index
      )[0]

    if (!previousPage) {
      return
    }

    dispatch(setAssignmentPageId(previousPage.id))
  }

  const onSelectQuestion = useCallback(
    (pageId: number, questionNumber: number, questionIndexInPage: number) => {
      const doesPageExist = assignment?.pages?.some(
        (page: AssignmentPage) => page.id === pageId
      )

      if (!doesPageExist) {
        return
      }

      setPendingQuestionScrollTarget({
        pageId,
        questionNumber,
        questionIndexInPage
      })

      dispatch(setAssignmentPageId(pageId))
    },
    [assignment?.pages, dispatch]
  )

  const onAssignmentAnswerChange = async (
    questionId: number,
    selectedChoice: number
  ) => {
    if (!currentPage?.id) {
      return
    }

    dispatch(
      updateAssignmentQuestionAnswer({
        pageId: currentPage.id,
        questionId,
        answer: selectedChoice
      })
    )

    const classroom_id = Cookies.get('classroom_id')
    const student_id = Cookies.get('student_id')

    socketRef.current?.emit(SOCKET_STUDENT_UPDATE_ANSWER, {
      classroom_id,
      answer: selectedChoice,
      test_type: 'assignment',
      test_id: id,
      question_id: questionId,
      student_id
    })

    await submitAssignmentPartialAnswers(id!, classroom_id!, {
      question_id: questionId,
      response: selectedChoice,
      time_in_minutes: 1,
      clear_answer: false
    })
  }

  const onClearPress = useCallback(() => {
    setShowClearModal(true)
  }, [])

  const onClearAndClose = useCallback(() => {
    setShowClearModal(false)
    dispatch(clearCurrentAssignmentPageAnswers())
  }, [dispatch])

  const onPauseExitPress = useCallback(() => {
    setShowPauseExitModal(true)
  }, [setShowPauseExitModal])

  const submitAssignment = useCallback(async () => {
    if (!id) {
      return
    }

    const classroomId = Cookies.get('classroom_id')
    if (!classroomId) {
      return
    }

    const answers =
      assignment?.pages
        ?.filter((page: AssignmentPage) => isQuestionSetPage(page))
        .flatMap((page: AssignmentPage) => {
          const bodyItems = page.body_items as Array<
            AssignmentBodyItem | QuestionBodyItem
          >

          return bodyItems
            .filter(
              (
                item: AssignmentBodyItem | QuestionBodyItem
              ): item is QuestionBodyItem =>
                item.body_item_type === 'question' && 'answer' in item
            )
            .map((item: QuestionBodyItem) => ({
              question_id: Number(item.id),
              selected_choice: Number(item.answer) || 0
            }))
        }) || []

    try {
      setIsSubmittingAssignment(true)
      await submitAssignmentAnswers(id, classroomId, answers)
      setShowFinishModal(false)
      navigate('/classroom')
    } catch (error) {
      console.error('Failed to submit assignment answers', error)
    } finally {
      setIsSubmittingAssignment(false)
    }
  }, [id, assignment?.pages, navigate])

  const onClearAssignmentAnswers = useCallback(() => {
    dispatch(clearAllAssignmentAnswers())
  }, [dispatch])

  const onFinishPress = useCallback(() => {
    setShowFinishModal(true)
  }, [])

  useEffect(() => {
    const socketInstance = socketRef.current
    const classroomId = Number(Cookies.get('classroom_id'))
    const studentId = Number(Cookies.get('id_student'))
    const assignmentId = Number(id)

    /**
     * Assignment students should react to instructor pause, clear, force-submit,
     * and unavailable events the same way benchmark and formative students do,
     * but only for the currently opened assignment session.
     */
    if (!isSocketReady || !socketInstance || !classroomId || !assignmentId) {
      return
    }

    const handleAssignmentClassEvent = (data: any) => {
      const resource = data?.resource

      if (!resource) {
        return
      }

      const resourceType = resource.type
      const resourceEvent = resource.event || ''
      const resourceClassroomId = Number(resource.classroom_id)
      const resourceTestId = Number(resource.test_id)

      if (
        resourceClassroomId !== classroomId ||
        resourceTestId !== assignmentId
      ) {
        return
      }

      if (resourceType === SOCKET_TEACHER_PAUSE_RESUME) {
        setIsPausedByTeacher(!isPausedByTeacher)
        return
      }

      if (resourceType === SOCKET_TEACHER_FORCE_SUBMIT) {
        if (!resource.student_id || Number(resource.student_id) === studentId) {
          submitAssignment()
        }
        return
      }

      if (resourceType === SOCKET_TEACHER_KICK_STUDENT) {
        if (!resource.student_id || Number(resource.student_id) === studentId) {
          setShowUnAvailableTestModal(true)
        }
        return
      }

      if (
        resourceType === SOCKET_TEACHER_CLEAR_ANSWER ||
        (resourceType === SOCKET_TEACHER_VIEW_UPDATE &&
          resourceEvent === 'clear_answers')
      ) {
        if (!resource.student_id || Number(resource.student_id) === studentId) {
          onClearAssignmentAnswers()
        }
      }
    }

    socketInstance.on(SOCKET_CLASS_BY_ID_CHANNEL, handleAssignmentClassEvent)

    return () => {
      socketInstance.off(SOCKET_CLASS_BY_ID_CHANNEL, handleAssignmentClassEvent)
    }
  }, [
    id,
    isPausedByTeacher,
    isSocketReady,
    onClearAssignmentAnswers,
    setIsPausedByTeacher,
    setShowUnAvailableTestModal,
    socketRef,
    SOCKET_CLASS_BY_ID_CHANNEL,
    SOCKET_TEACHER_CLEAR_ANSWER,
    SOCKET_TEACHER_FORCE_SUBMIT,
    SOCKET_TEACHER_KICK_STUDENT,
    SOCKET_TEACHER_PAUSE_RESUME,
    SOCKET_TEACHER_VIEW_UPDATE,
    submitAssignment
  ])

  useEffect(() => {
    const classroom_id = Cookies.get('classroom_id')
    const socketInstance = socketRef.current
    const student_id = Cookies.get('student_id')

    /**
     * Assignment reloads can land after the socket instance already exists but
     * before the room subscription is restored. Emit the join event right away
     * when connected, and also on each reconnect so the assignment returns to
     * its classroom room after a hard refresh or transient socket reconnect.
     */
    if (!isSocketReady || !socketInstance || !classroom_id || !student_id || !id) {
      return
    }

    const emitAssignmentJoinRoom = () => {
      socketInstance.emit(SOCKET_STUDENT_JOIN_ROOM, {
        classroom_id,
        student_id,
        test_type: 'assignment',
        test_id: id
      })
    }

    if (socketInstance.connected) {
      emitAssignmentJoinRoom()
    }

    socketInstance.on('connect', emitAssignmentJoinRoom)

    return () => {
      socketInstance.off('connect', emitAssignmentJoinRoom)
    }
  }, [id, isSocketReady, socketRef, SOCKET_STUDENT_JOIN_ROOM])

  const onToggleAssignmentBookmark = useCallback(
    async (nextBookmarkedState: boolean) => {
      if (!currentPage?.id || !id) {
        return
      }

      const classroomId = Cookies.get('classroom_id')
      if (!classroomId) {
        return
      }

      dispatch(
        updateAssignmentPageAction({
          ...currentPage,
          is_bookmarked: nextBookmarkedState
        })
      )

      try {
        const serializedPenMarkings = JSON.stringify(
          currentPage.student_pen_note_markings || null
        )

        await upsertAssignmentAnnotations(classroomId, id, {
          page_id: currentPage.id,
          is_bookmarked: nextBookmarkedState,
          student_notes: currentPage.student_notes || '',
          student_pen_note_markings: serializedPenMarkings
        })

        // to automatically update the toc state from api
        await dispatch(getAssignmentDataAction(id, classroomId))
        await dispatch(setAssignmentPageId(currentPage.id))
      } catch (error) {
        dispatch(updateAssignmentPageAction(currentPage))
        console.error('Failed to upsert assignment annotations', error)
      }
    },
    [currentPage, dispatch, id]
  )

  useEffect(() => {
    if (!pendingQuestionScrollTarget || !currentPage) {
      return
    }

    if (currentPage.id !== pendingQuestionScrollTarget.pageId) {
      return
    }

    const scrollToQuestion = () => {
      const byOrderIndex = document.querySelector(
        `[data-question-order-index="${pendingQuestionScrollTarget.questionNumber}"]`
      ) as HTMLElement | null

      const questionItems = document.querySelectorAll(
        '[data-assignment-question="true"]'
      )

      const byQuestionPosition = questionItems[
        Math.max(pendingQuestionScrollTarget.questionIndexInPage, 0)
      ] as HTMLElement | undefined

      const target = byOrderIndex || byQuestionPosition || null

      if (!target) {
        return false
      }

      target.scrollIntoView({ behavior: 'smooth', block: 'center' })
      return true
    }

    if (scrollToQuestion()) {
      setPendingQuestionScrollTarget(null)
      return
    }

    const retryTimeout = window.setTimeout(() => {
      if (scrollToQuestion()) {
        setPendingQuestionScrollTarget(null)
      }
    }, 50)

    return () => {
      window.clearTimeout(retryTimeout)
    }
  }, [currentPage, pendingQuestionScrollTarget])

  /**
   * Pagination controls for the assignment pages.
   * The next and back buttons will be disabled based on the current page index
   * and the total number of pages in the assignment.
   */
  const isLastPage = pageIdx === total_pages - 1
  const isFirstPage = pageIdx === 0

  const allAssignmentQuestionIds = useMemo(() => {
    const questionSetPages = (assignment?.pages || []).filter(
      (page: AssignmentPage) => isQuestionSetPage(page)
    )

    if (!questionSetPages.length) {
      return [] as number[]
    }

    const questionIds: number[] = questionSetPages.flatMap(
      (page: AssignmentPage) =>
        (page.body_items as Array<AssignmentBodyItem | QuestionBodyItem>)
          .filter(
            (
              item: AssignmentBodyItem | QuestionBodyItem
            ): item is QuestionBodyItem =>
              item.body_item_type === 'question' && 'answer' in item
          )
          .map((item: QuestionBodyItem) => Number(item.id))
    )

    return Array.from(new Set<number>(questionIds))
  }, [assignment?.pages])

  const hasCurrentPageAnswer = useMemo(() => {
    /**
     * The active page now owns the selected answers, so filled-choice state and
     * clear availability should come directly from the page body items.
     */
    if (!currentPage?.body_items?.length) {
      return false
    }

    return currentPage.body_items.some(
      (item: AssignmentBodyItem | QuestionBodyItem): item is QuestionBodyItem =>
        item.body_item_type === 'question' &&
        'answer' in item &&
        Number.isInteger(item.answer)
    )
  }, [currentPage])

  const completedQuestionCount = useMemo(() => {
    if (!assignment?.pages?.length) {
      return 0
    }

    return assignment.pages
      .filter((page: AssignmentPage) => isQuestionSetPage(page))
      .flatMap((page: AssignmentPage) => page.body_items)
      .filter(
        (
          item: AssignmentBodyItem | QuestionBodyItem
        ): item is QuestionBodyItem =>
          item.body_item_type === 'question' &&
          'answer' in item &&
          Number.isInteger(item.answer)
      ).length
  }, [assignment?.pages])

  const assignmentAssessmentName = currentPage
    ? `${assessmentName.split('-')?.[0]} - ${currentPage?.title}`
    : assessmentName

  return (
    <AssessmentContainer>
      <PageContent>
        <Header
          onQuestionSelector={() => setShowQuestionSelectorModal(true)}
          studentPages={headerTotalPages}
          currentPage={headerCurrentPage}
          assessmentType='assignments'
          assessmentName={assignmentAssessmentName}
          onToggleAssignmentBookmark={onToggleAssignmentBookmark}
        />
        <MainAssignmentSection
          currentPage={currentPage}
          viewMode={ViewMode.Student}
          onAnswerChange={onAssignmentAnswerChange}
        />

        {assignment?.toc?.main?.length > 0 && (
          <AssignmentTocModal
            viewMode={ViewMode.Student}
            assessmentName={assessmentName}
            currentPageId={currentPage?.id}
            visible={showQuestionSelectorModal}
            main={assignment?.toc.main}
            onSelectQuestion={onSelectQuestion}
            onClose={() => setShowQuestionSelectorModal(false)}
          />
        )}

        <Footer
          type='assignments'
          onNext={onNextPagePress}
          onBack={onBackQuestionPress}
          onClear={onClearPress}
          onFinish={onFinishPress}
          disableFinish={!isLastPage}
          disableClear={!hasCurrentPageAnswer}
          onPauseExit={onPauseExitPress}
          disableBack={isFirstPage}
          disableNext={isLastPage}
        />

        <ClearModal
          show={showClearModal}
          onClose={() => setShowClearModal(false)}
          onConfirmClear={onClearAndClose}
        />

        <PauseExitModal />
        <UnAvailableTestModal />

        <FinishModal
          show={showFinishModal}
          setShow={setShowFinishModal}
          questionCompleted={completedQuestionCount}
          totalQuestions={allAssignmentQuestionIds.length}
          onFinish={submitAssignment}
          submitLoading={isSubmittingAssignment}
        />
      </PageContent>
    </AssessmentContainer>
  )
}

export default AssignmentContainer
